/**
 * Notify — public "email me my manage link" page (subscriber-self-service.md Part A). Route
 * `/notify/manage` (standalone) and `/notify/manage?embed=1` (widget iframe variant, ADR-0004,
 * same useEmbedClose + no-TownHeader-in-embed pattern as the other notify pages). A single email
 * field + the reused CaptchaField → submit calls `manage-link`.
 *
 * Anti-enumeration (spec): the success screen is IDENTICAL regardless of whether the address is on
 * file, subscribed, pending, or unknown — the server always resolves {status:"ok"} and the UI never
 * branches on the response shape. Styled/structured like SubscribePage (♻️ same td-intake/td-confirm
 * classes, optimistic + global-progress-bar + error-toast convention, WCAG 2.1 AA labels/error
 * summary/focus management).
 */
import { useEffect, useRef, useState } from "react";
import { manageLink, ApiError } from "../../api/index.js";
import { Field } from "../../components/Field.js";
import { Button } from "../../components/Button.js";
import { CaptchaField } from "../../components/CaptchaField.js";
import { CheckIcon, SpinnerIcon } from "../../components/icons.js";
import { TownHeader } from "../../components/TownHeader.js";
import { ResidentThemeRoot } from "../../components/ResidentThemeRoot.js";
import { useTheme } from "../../theme/ThemeProvider.js";
import { useDocumentTitle, pageTitle } from "../../components/useDocumentTitle.js";
import { useEmbedClose } from "../../components/useEmbedClose.js";
import { useCaptcha } from "../../components/useCaptcha.js";
import { CAPTCHA_ENABLED } from "../../api/wafCaptcha.js";

const ID = {
  email: "field-manage-email",
  captcha: "field-manage-captcha",
};

interface Props {
  embed?: boolean;
}

type SubmitState = "idle" | "submitting" | "error";

export function ManageLinkPage({ embed = false }: Props): JSX.Element {
  const { config } = useTheme();
  useDocumentTitle(pageTitle("Manage Your Updates", config.townName));
  useEmbedClose(embed);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const captcha = useCaptcha();
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [submitErrorMsg, setSubmitErrorMsg] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (submitted) headingRef.current?.focus();
  }, [submitted]);

  const requiredMissing = !isValidEmail(email) || !captcha.passed;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitState === "submitting") return;

    const trimmed = email.trim();
    if (!isValidEmail(trimmed)) {
      setEmailError("Enter a valid email address, like name@example.com.");
      return;
    }
    setEmailError(null);
    if (!CAPTCHA_ENABLED && !captcha.passed) {
      setSubmitErrorMsg(null);
      setSubmitState("error");
      return;
    }
    // The puzzle renders inline on mount; ensureForSubmit is a fast no-op when it's already solved &
    // fresh, and re-challenges only if the token is missing/expired (useCaptcha). The solved token
    // then rides the POST via the x-aws-waf-token header (client.ts).
    if (!(await captcha.ensureForSubmit())) return;
    setSubmitState("submitting");
    setSubmitErrorMsg(null);
    try {
      // Anti-enumeration: the response is ALWAYS {status:"ok"} — never branch on it.
      await manageLink(trimmed);
      setSubmitted(true);
      setSubmitState("idle");
    } catch (err) {
      setSubmitState("error");
      setSubmitErrorMsg(
        err instanceof ApiError ? err.message : "We couldn't submit that — try again.",
      );
    }
  };

  if (submitted) {
    return (
      <ResidentThemeRoot className={`td-intake${embed ? " td-intake--embed" : ""}`}>
        <TownHeader embed={embed} />
        <main className="td-confirm">
          <p className="td-sr-only" aria-live="polite">
            If that address is on our list, we&apos;ve emailed a link to manage your preferences.
          </p>
          <div className="td-confirm__mark" aria-hidden="true">
            <CheckIcon width={28} height={28} />
          </div>
          <h1 className="td-confirm__title" tabIndex={-1} ref={headingRef}>
            Check your email.
          </h1>
          <p className="td-confirm__sub">
            If that address is on our list, we&apos;ve emailed you a link to manage your
            preferences.
          </p>
        </main>
        <footer className="td-footer">
          <span>
            Powered by{" "}
            <a href="https://towndesk.us" target="_blank" rel="noopener noreferrer">
              TownDesk
            </a>
          </span>
          {config.privacyUrl ? (
            <a href={config.privacyUrl} rel="noopener">
              Privacy
            </a>
          ) : null}
        </footer>
      </ResidentThemeRoot>
    );
  }

  return (
    <ResidentThemeRoot className={`td-intake${embed ? " td-intake--embed" : ""}`}>
      <a href="#td-manage-form" className="td-skip-link">
        Skip to the manage-updates form
      </a>
      <TownHeader embed={embed} />

      <main className="td-intake__main">
        <h1 className="td-intake__title">Manage your updates.</h1>
        <p className="td-header__subcopy">
          Lost your link? Enter the email address you subscribed with and we&apos;ll send you a
          link to manage your {config.townName} updates.
        </p>

        {submitState === "error" && submitErrorMsg ? (
          <div className="td-alert td-alert--danger" role="alert">
            <p>{submitErrorMsg}</p>
          </div>
        ) : null}

        <form
          id="td-manage-form"
          className="td-form"
          noValidate
          onSubmit={onSubmit}
          aria-busy={submitState === "submitting"}
        >
          <Field
            id={ID.email}
            label="Email"
            required
            error={emailError ?? undefined}
            helper="We'll only use this to send you your manage-updates link."
          >
            {(aria) => (
              <input
                {...aria}
                className="td-input"
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError(null);
                }}
              />
            )}
          </Field>

          <CaptchaField
            ref={captcha.containerRef}
            id={ID.captcha}
            status={captcha.status}
            error={captcha.error ?? undefined}
            passed={captcha.passed}
            onChange={captcha.onChange}
            onRetry={captcha.retry}
          />

          <div className="td-submit">
            <Button
              type="submit"
              fullWidth
              disabled={submitState === "submitting"}
              aria-disabled={requiredMissing || submitState === "submitting"}
            >
              {submitState === "submitting" ? (
                <>
                  <SpinnerIcon width={18} height={18} />
                  Sending…
                </>
              ) : (
                "Email me my link"
              )}
            </Button>
          </div>
        </form>
      </main>

      <footer className="td-footer">
        <span>
          Powered by{" "}
          <a href="https://towndesk.us" target="_blank" rel="noopener noreferrer">
            TownDesk
          </a>
        </span>
        {config.privacyUrl ? (
          <a href={config.privacyUrl} rel="noopener">
            Privacy
          </a>
        ) : null}
      </footer>
    </ResidentThemeRoot>
  );
}

function isValidEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}
